import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { Toast, Field, Modal, PageHeader, BtnPrimary, BtnSecondary, LoadingSpinner } from './v2-utils';
import '../styles/v2.css';

const DRAFT_KEY    = 'edit_unit_draft';
const SUPPLIER_KEY = 'edit_unit_last_supplier_id';

export default function EditUnitV2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [products, setProducts]           = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [unitType, setUnitType]           = useState('');
  const [unitCategory, setUnitCategory]   = useState('buying');
  const [existingUnits, setExistingUnits] = useState([]);
  const [oppositeUnitId, setOppositeUnitId] = useState('');
  const [conversionRate, setConversionRate] = useState('');
  const [suppliers, setSuppliers]         = useState([]);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]);
  const [primarySupplierId, setPrimarySupplierId] = useState('');
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [toast, setToast]                 = useState(null);
  const [addSupplierOpen, setAddSupplierOpen] = useState(false);
  const [originalUnit, setOriginalUnit]   = useState(null);

  /* restore draft when returning from Add Supplier */
  useEffect(() => {
    const json = sessionStorage.getItem(DRAFT_KEY);
    if (!json) return;
    try {
      const d = JSON.parse(json);
      setSelectedProductId(d.selectedProductId || '');
      setUnitType(d.unitType || '');
      setOppositeUnitId(d.oppositeUnitId || '');
      setConversionRate(d.conversionRate || '');
      const base = d.selectedSupplierIds || [];
      const newSup = sessionStorage.getItem(SUPPLIER_KEY);
      if (newSup) { const merged = base.includes(newSup) ? base : [...base, newSup]; setSelectedSupplierIds(merged); setPrimarySupplierId(newSup); }
      else { setSelectedSupplierIds(base); setPrimarySupplierId(d.primarySupplierId || ''); }
    } catch {}
    finally { sessionStorage.removeItem(DRAFT_KEY); sessionStorage.removeItem(SUPPLIER_KEY); }
  }, []);

  /* keep primarySupplierId valid */
  useEffect(() => {
    if (primarySupplierId && !selectedSupplierIds.includes(primarySupplierId)) setPrimarySupplierId('');
    if (selectedSupplierIds.length === 1 && !primarySupplierId) setPrimarySupplierId(selectedSupplierIds[0]);
  }, [selectedSupplierIds, primarySupplierId]);

  /* initial load */
  useEffect(() => {
    const load = async () => {
      try {
        const [prodsRes, unitRes, supRes] = await Promise.all([
          axiosInstance.get('/products'),
          axiosInstance.get(`/units/${id}`),
          axiosInstance.get('/suppliers'),
        ]);
        setProducts(prodsRes.data || []);
        setSuppliers((supRes.data || []).filter(s => !s.market_name));
        const unit = unitRes.data;
        setOriginalUnit(unit);
        setUnitType(unit.unit_type || '');
        setUnitCategory(unit.unit_category || 'buying');
        setConversionRate(unit.conversion_rate != null ? String(unit.conversion_rate) : '');
        if (unit.product_id) {
          setSelectedProductId(String(unit.product_id));
          const unitsRes = await axiosInstance.get(`/units/product/${unit.product_id}`);
          const others = (unitsRes.data || []).filter(u => String(u.unit_id) !== String(id));
          setExistingUnits(others);
          const opp = others.find(u => u.unit_id === unit.opposite_unit_id) || others[0];
          if (opp) setOppositeUnitId(String(opp.unit_id));
        }
        /* load supplier links */
        if (unit.supplier_ids?.length) {
          setSelectedSupplierIds(unit.supplier_ids.map(String));
          if (unit.supplier_id) setPrimarySupplierId(String(unit.supplier_id));
        } else if (unit.supplier_id) {
          setSelectedSupplierIds([String(unit.supplier_id)]);
          setPrimarySupplierId(String(unit.supplier_id));
        }
      } catch { setToast({ message: 'Failed to load unit data.', severity: 'error' }); }
      finally { setLoading(false); }
    };
    load();
  }, [id]);

  const handleProductChange = async (productId) => {
    setSelectedProductId(productId);
    setOppositeUnitId('');
    if (!productId) { setExistingUnits([]); return; }
    try {
      const res = await axiosInstance.get(`/units/product/${productId}`);
      setExistingUnits((res.data || []).filter(u => String(u.unit_id) !== String(id)));
    } catch {}
  };

  const handleSaveAndAddSupplier = () => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ selectedProductId, unitType, oppositeUnitId, conversionRate, selectedSupplierIds, primarySupplierId }));
    } catch {}
    setAddSupplierOpen(false);
    navigate('/new/dashboard/suppliers/add', { state: { from: 'edit_unit', unitId: id } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (unitCategory === 'buying' && selectedSupplierIds.length > 0 && !primarySupplierId) {
      setToast({ message: 'Please select a Most Recent Supplier from the selected list.', severity: 'error' }); return;
    }
    setSaving(true);
    try {
      await axiosInstance.put(`/units/${id}`, {
        product_id: selectedProductId,
        unit_type: unitType,
        unit_category: unitCategory,
        opposite_unit_id: oppositeUnitId || null,
        conversion_rate: conversionRate,
      });
      if (unitCategory === 'buying') {
        await axiosInstance.put(`/units/${id}/suppliers`, { supplier_ids: selectedSupplierIds.map(Number) }).catch(() => {});
        if (primarySupplierId) await axiosInstance.put(`/units/${id}/primary-supplier`, { supplier_id: Number(primarySupplierId) }).catch(() => {});
      }
      setToast({ message: 'Unit updated successfully. Note: reconcile inventory for changes to reflect accurately.', severity: 'success' });
      setTimeout(() => navigate('/new/dashboard/units/view'), 3000);
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to update unit.', severity: 'error' });
    } finally { setSaving(false); }
  };

  const oppUnit = existingUnits.find(u => String(u.unit_id) === String(oppositeUnitId));

  if (loading) return <LoadingSpinner message="Loading unit…" />;

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <PageHeader title="Edit Unit" onBack={() => navigate('/new/dashboard/units/view')} />
      <form onSubmit={handleSubmit}>
        <div className="v2-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Unit Details</p>

          <Field label="Product *">
            <div style={{ position: 'relative' }}>
              <select className="v2-input" style={{ paddingRight: 36, appearance: 'none', cursor: 'pointer' }}
                value={selectedProductId} onChange={e => handleProductChange(e.target.value)} required>
                <option value="">Select product…</option>
                {products.map(p => <option key={p.product_id} value={String(p.product_id)}>{p.product_name}{p.brand ? ` (${p.brand})` : ''}{p.variety ? ` - ${p.variety}` : ''}</option>)}
              </select>
              <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
            </div>
          </Field>

          <Field label="Unit Type *">
            <input className="v2-input" type="text" value={unitType} onChange={e => setUnitType(e.target.value)} required placeholder="e.g. Bottle, Crate, Kg" />
          </Field>

          <Field label="Comparison Unit *" hint="Which existing unit is this compared against?">
            <div style={{ position: 'relative' }}>
              <select className="v2-input" style={{ paddingRight: 36, appearance: 'none', cursor: 'pointer' }}
                value={oppositeUnitId} onChange={e => setOppositeUnitId(e.target.value)} required disabled={!existingUnits.length}>
                <option value="">Select comparison unit…</option>
                {existingUnits.map(u => <option key={u.unit_id} value={String(u.unit_id)}>{u.unit_type} ({u.unit_category})</option>)}
              </select>
              <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
            </div>
          </Field>

          <Field label={`How many ${oppUnit?.unit_type || 'comparison unit'} are in ${unitType || 'this unit'}? *`}>
            <input className="v2-input" type="number" value={conversionRate} onChange={e => setConversionRate(e.target.value)} min="0" step="0.01" required />
          </Field>

          {/* Suppliers section — buying units only */}
          {unitCategory === 'buying' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Suppliers (optional)</p>
              <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                {suppliers.map(s => (
                  <label key={s.supplier_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid #f1f5f1', cursor: 'pointer', background: selectedSupplierIds.includes(String(s.supplier_id)) ? '#f0fdf4' : 'white' }}>
                    <input type="checkbox" checked={selectedSupplierIds.includes(String(s.supplier_id))}
                      onChange={e => { const sid = String(s.supplier_id); setSelectedSupplierIds(prev => e.target.checked ? [...prev, sid] : prev.filter(x => x !== sid)); }}
                      style={{ accentColor: '#10b981', width: 16, height: 16 }} />
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#334155', flex: 1 }}>{s.name || s.supplier_name || 'Unnamed'}</span>
                    {selectedSupplierIds.includes(String(s.supplier_id)) && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#065f46', cursor: 'pointer' }}>
                        <input type="radio" name="primary" value={String(s.supplier_id)} checked={primarySupplierId === String(s.supplier_id)} onChange={() => setPrimarySupplierId(String(s.supplier_id))} style={{ accentColor: '#10b981' }} />
                        Primary
                      </label>
                    )}
                  </label>
                ))}
                <button type="button" onClick={() => setAddSupplierOpen(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#10b981', fontWeight: 600 }}>
                  <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                  Add New Supplier
                </button>
              </div>
            </div>
          )}

          <button type="submit" className="v2-btn-submit" disabled={saving} style={{ fontSize: 15 }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>

      <Modal open={addSupplierOpen} onClose={() => setAddSupplierOpen(false)} title="Add New Supplier"
        footer={<><BtnSecondary onClick={() => setAddSupplierOpen(false)}>Cancel</BtnSecondary><BtnPrimary onClick={handleSaveAndAddSupplier}>Go to Add Supplier</BtnPrimary></>}>
        <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, margin: 0 }}>Your current changes will be saved and restored when you return.</p>
      </Modal>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
